import moment from "moment";
import React, { useState } from "react";
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  Table,
  Timeline,
  TimelineBody,
  TimelineContent,
  TimelineItem,
  TimelinePoint,
  TimelineTime,
  TimelineTitle,
} from "flowbite-react";
import { FaArrowsLeftRight } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN, CATEGORIES, LOAN } from "../../config/apiConfig";
import Drawer from "../Drawer";
import { formatToReal } from "../../utils/utils";
import {
  FaBuilding,
  FaCheck,
  FaCity,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhone,
  FaRegClock,
  FaRegEdit,
  FaRegEye,
  FaRegTrashAlt,
} from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import QueryEditForm from "../forms/QueryEditForm";
import TableLoading from "../TableLoading";
import { Tooltip } from "react-tooltip";
import CreateQueryForm from "../forms/CreateQueryForm";
import EditQueryCustomForm from "../forms/EditQueryCustomForm";
import TitleStripe from "../common/TitleStripe";
import DrawerLoading from "../DrawerLoading";
import EmptyTable from "./EmptyTable";
import ModalDelete from "../modals/ModalDelete";

const InfoRow = ({ label, icon: Icon, children, className = "" }) => (
  <div className={`flex gap-3 items-start ${className}`}>
    {Icon && <Icon className="text-gray-500 mt-0.5 text-lg flex-shrink-0" />}
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
        {label}
      </span>
      <div className="text-sm font-semibold text-slate-900">{children}</div>
    </div>
  </div>
);

const SimulationsTable = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const onPageChange = (page) => setCurrentPage(page);
  const [sortByPrice, setSortByPrice] = useState("");
  const [searchName, setSearchName] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [minAmountFilter, setMinAmountFilter] = useState("");
  const [maxAmountFilter, setMaxAmountFilter] = useState("");
  const [fromDateFilter, setFromDateFilter] = useState("");
  const [toDateFilter, setToDateFilter] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    searchName: "",
    status: "",
    minAmount: "",
    maxAmount: "",
    fromDate: "",
    toDate: "",
  });
  const [isDrawerEditQuery, setIsDrawerEditQuery] = useState(false);
  const [isDrawerEditLoan, setIsDrawerEditLoan] = useState(false);
  const [isDrawerCreateSimulation, setIsDrawerCreateSimulation] =
    useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [selectedLoanToDelete, setSelectedLoanToDelete] = useState(null);
  const [isDeletingSimulation, setIsDeletingSimulation] = useState(false);
  const [averageCredit, setAverageCredit] = useState(0);

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const rejectionOptions = [
    "Documento ilegível",
    "Documento cortado",
    "Documento fora do padrão",
    "Documento vencido",
    "Documento não corresponde ao tipo solicitado",
  ];

  const openRejectModal = (docId) => {
    setSelectedDocumentId(docId);
    setIsRejectModalOpen(true);
  };

  const confirmRejection = async () => {
    if (!rejectionReason) return alert("Selecione o motivo da rejeição.");
    try {
      await axios.post(`${LOAN.INVALIDATE_DOCUMENT}/${selectedDocumentId}`, {
        reason: rejectionReason,
      });
      await fetchLoanDetails(selectedLoan.identifier, selectedLoan.accessKey);
      setIsRejectModalOpen(false);
      setRejectionReason("");
    } catch (err) {
      console.error("Erro ao rejeitar documento:", err.message);
    }
  };

  const {
    isLoading: isLoadingLoans,
    error: errorLoans,
    data: loans,
  } = useQuery(
    [
      "loans",
      currentPage,
      sortByPrice,
      appliedFilters.searchName,
      appliedFilters.status,
      appliedFilters.minAmount,
      appliedFilters.maxAmount,
      appliedFilters.fromDate,
      appliedFilters.toDate,
    ],
    async () => {
      const params = new URLSearchParams();
      params.set("page", currentPage);
      if (sortByPrice) {
        params.set("sortByPrice", sortByPrice);
      }

      const addParam = (key, value) => {
        if (value !== "" && value !== null && value !== undefined) {
          params.set(key, value);
        }
      };

      const {
        searchName: appliedSearchName,
        status,
        minAmount,
        maxAmount,
        fromDate,
        toDate,
      } = appliedFilters;

      addParam("searchName", appliedSearchName);
      addParam("status", status);
      addParam("minAmount", minAmount);
      addParam("maxAmount", maxAmount);
      addParam("fromDate", fromDate);
      addParam("toDate", toDate);

      const { data } = await axios.get(`${LOAN.GET_ALL}?${params.toString()}`);
      setTotalPages(data.data.totalPages || 1);
      setTotalCount(data.data.totalCount || 0);
      setAverageCredit(Number(data.data.averageAmount) || 0);
      return data.data.data;
    }
  );

  const { isLoading: isLoadingDocumentTypes, data: documentTypes } = useQuery(
    ["documentTypes"],
    async () => {
      const { data } = await axios.get(`${LOAN.GET_DOCUMENT_TYPES}`);
      return data.data;
    }
  );

  const fetchLoanDetails = async (loanIdentifier, accessKey) => {
    const { data } = await axios.get(
      `${LOAN.GET_LOAN_BY_IDENTIFIER}/${loanIdentifier}?accessKey=${accessKey}`
    );
    setSelectedLoan(data.data);
  };

  const { data: categories } = useQuery(["categories"], async () => {
    const { data } = await axios.get(`${CATEGORIES.GET_ALL}`);
    return data.data.data;
  });

  const applyFilters = (event) => {
    event.preventDefault();
    setCurrentPage(1);
    setAppliedFilters({
      searchName: searchName.trim(),
      status: statusFilter,
      minAmount: minAmountFilter,
      maxAmount: maxAmountFilter,
      fromDate: fromDateFilter,
      toDate: toDateFilter,
    });
  };

  const resetFilters = () => {
    setSearchName("");
    setStatusFilter("");
    setMinAmountFilter("");
    setMaxAmountFilter("");
    setFromDateFilter("");
    setToDateFilter("");
    setAppliedFilters({
      searchName: "",
      status: "",
      minAmount: "",
      maxAmount: "",
      fromDate: "",
      toDate: "",
    });
    setCurrentPage(1);
  };

  const handleSortChange = (value) => {
    setSortByPrice(value);
    setCurrentPage(1);
  };

  const handleViewSimulation = async (loan) => {
    await fetchLoanDetails(loan.identifier, loan.accessKey);
    setIsOpenModal(true);
  };

  const handleEditSimulation = (query) => {
    setSelectedLoan(query);
    query?.queryCustomizedId
      ? setIsDrawerEditLoan(true)
      : setIsDrawerEditQuery(true);
  };

  const handleDeleteSimulation = (loan) => {
    setSelectedLoanToDelete(loan);
    setIsOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedLoanToDelete(null);
    setIsDeletingSimulation(false);
  };

  const confirmDeleteSimulation = async () => {
    if (!selectedLoanToDelete) return;
    try {
      setIsDeletingSimulation(true);
      await axios.delete(`${LOAN.DELETE}/${selectedLoanToDelete.id}`);
      queryClient.invalidateQueries([
        "loans",
        currentPage,
        sortByPrice,
        appliedFilters.searchName,
        appliedFilters.status,
        appliedFilters.minAmount,
        appliedFilters.maxAmount,
        appliedFilters.fromDate,
        appliedFilters.toDate,
      ]);
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Erro ao excluir a simulação:", error);
      alert("Não foi possível excluir a simulação.");
    } finally {
      setIsDeletingSimulation(false);
    }
  };

  const isUploaded = (docTypeId, partnerId = null) =>
    selectedLoan?.Documents?.some(
      (doc) =>
        doc.documentTypeId === docTypeId &&
        (doc.partnerId ?? null) === (partnerId ?? null)
    );

  const getDocsByAppliesTo = (appliesTo, partnerId = null) =>
    documentTypes
      ?.filter(
        (docType) =>
          docType.appliesTo === appliesTo || docType.appliesTo === "both"
      )
      ?.map((docType) => ({
        ...docType,
        uploaded: isUploaded(docType.id, partnerId),
      }));

  const getUploadedDoc = (docTypeId, partnerId = null) =>
    selectedLoan?.Documents?.find(
      (doc) =>
        doc.documentTypeId === docTypeId &&
        (doc.partnerId ?? null) === (partnerId ?? null)
    );

  const countDocs = (appliesTo, partnerId = null) => {
    const docs = getDocsByAppliesTo(appliesTo, partnerId);
    const total = docs.length;
    const enviados = docs.filter((doc) => doc.uploaded).length;
    return `${enviados}/${total}`;
  };

  return (
    <>
      <div className="flex flex-col gap-3 mb-3 mt-2">
        <div className="flex justify-end">
          <Button
            className="primary"
            onClick={() => setIsDrawerCreateSimulation(true)}
          >
            Criar nova simulação
          </Button>
        </div>
        <form
          className="w-full rounded-xl border border-[#d6cfc5] bg-white p-4 shadow-sm"
          onSubmit={applyFilters}
        >
          <div className="grid gap-3 text-gray-700 md:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-[0.3em] text-gray-600">
                Cliente ou responsável
              </label>
              <input
                type="text"
                value={searchName}
                onChange={(event) => setSearchName(event.target.value)}
                placeholder="Buscar por nome ou responsável"
                className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-[0.3em] text-gray-600">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
              >
                <option value="">Todos</option>
                <option value="0">Pendente</option>
                <option value="1">Aprovado</option>
                <option value="2">Reprovado</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-[0.3em] text-gray-600">
                Ordenar por valor
              </label>
              <select
                value={sortByPrice}
                onChange={({ target }) => handleSortChange(target.value)}
                className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
              >
                <option value="">Mais recentes</option>
                <option value="DESC">Maior valor</option>
                <option value="ASC">Menor valor</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-[0.3em] text-gray-600">
                Faixa de crédito (R$)
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  value={minAmountFilter}
                  onChange={(event) => setMinAmountFilter(event.target.value)}
                  placeholder="Mínimo"
                  className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
                />
                <input
                  type="number"
                  value={maxAmountFilter}
                  onChange={(event) => setMaxAmountFilter(event.target.value)}
                  placeholder="Máximo"
                  className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-[0.3em] text-gray-600">
                Período (criação)
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={fromDateFilter}
                  onChange={(event) => setFromDateFilter(event.target.value)}
                  className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
                />
                <input
                  type="date"
                  value={toDateFilter}
                  onChange={(event) => setToDateFilter(event.target.value)}
                  className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1 md:col-span-2 lg:col-span-1">
              <label className="text-[11px] uppercase tracking-[0.3em] text-transparent">
                Ações
              </label>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" type="submit">
                  Aplicar filtros
                </Button>
                <Button
                  size="sm"
                  color="light"
                  type="button"
                  onClick={resetFilters}
                >
                  Limpar
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className="grid gap-3 mt-3 md:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
            Total de simulações
          </span>
          <span className="text-2xl font-semibold text-gray-900">
            {totalCount}
          </span>
          <span className="text-xs text-gray-500">
            Atualizado com a página atual
          </span>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
            Valor médio de crédito
          </span>
          <span className="text-2xl font-semibold text-green-600">
            {formatToReal(averageCredit)}
          </span>
          <span className="text-xs text-gray-500">
            Média entre as simulações carregadas
          </span>
        </div>
      </div>

      <small
        className="gap-2 items-center mt-5 mb-1 flex md:hidden justify-center"
        style={{ fontSize: 10 }}
      >
        <span>Deslize para o lado para visualizar todas as informações</span>{" "}
        <FaArrowsLeftRight />
      </small>
      {loans?.length > 0 ? (
        <div className="mt-0 md:mt-5 overflow-x-auto">
          <div className="min-w-full rounded-xl border border-[#e9e4df] bg-white shadow-sm overflow-hidden">
            <Table striped={false} className="text-xs xl:text-sm">
              {/* Cabeçalho sticky */}
              {loans?.length > 0 || isLoadingLoans ? (
                <Table.Head className="uppercase text-[11px] tracking-wide sticky top-0 z-10">
                  <Table.HeadCell className="text-center bg-[#f3efeb] text-[#6b5f57] font-semibold">
                    ID
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-[#f3efeb] text-[#6b5f57] font-semibold">
                    Razão Social
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-[#f3efeb] text-[#6b5f57] font-semibold">
                    Cod. Acesso
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-[#f3efeb] text-[#6b5f57] font-semibold">
                    Data criação
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-[#f3efeb] text-[#6b5f57] font-semibold">
                    Valor
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-[#f3efeb] text-[#6b5f57] font-semibold">
                    Ações
                  </Table.HeadCell>
                </Table.Head>
              ) : null}

              <Table.Body className="divide-y divide-gray-100">
                {!isLoadingLoans ? (
                  loans?.length > 0 ? (
                    loans.map((loan, index) => (
                      <Table.Row
                        key={index}
                        className={`transition ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        } hover:bg-[#faf7f4]`}
                      >
                        {/* ID */}
                        <Table.Cell className="text-center font-medium text-gray-900 py-2">
                          #{loan.id}
                        </Table.Cell>

                        {/* Razão Social */}
                        <Table.Cell className="font-medium text-gray-900 py-2">
                          <span className="truncate block max-w-[220px]">
                            {loan?.Company?.name}
                          </span>
                        </Table.Cell>

                        {/* Código de acesso */}
                        <Table.Cell className="text-center font-medium text-gray-900 py-2">
                          {loan.accessKey}
                        </Table.Cell>

                        {/* Data de criação */}
                        <Table.Cell className="text-center font-medium text-gray-900 py-2">
                          <div>
                            {moment(loan?.createdAt).format("DD/MM/yyyy")}
                          </div>
                          <Badge
                            color="gray"
                            className="mt-0.5 text-[11px] font-normal w-fit mx-auto"
                          >
                            {moment().diff(moment(loan?.createdAt), "days") ===
                            0
                              ? "Hoje"
                              : `${moment().diff(
                                  moment(loan?.createdAt),
                                  "days"
                                )} dias atrás`}
                          </Badge>
                        </Table.Cell>

                        {/* Valor */}
                        <Table.Cell className="text-center font-medium text-gray-900 py-2">
                          {formatToReal(loan?.amount)}
                        </Table.Cell>

                        {/* Ações */}
                        <Table.Cell className="text-center py-2">
                          <div className="flex items-center justify-center gap-2">
                            {/* Visualizar */}
                            <Tooltip
                              id="tt-view"
                              content="Visualizar Simulação"
                            />
                            <Tooltip
                              id="tt-delete"
                              content="Excluir simulação"
                            />
                            <button
                              data-tooltip-id="tt-view"
                              onClick={() => handleViewSimulation(loan)}
                              className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition"
                            >
                              <FaRegEye size={16} />
                            </button>
                            <button
                              data-tooltip-id="tt-delete"
                              onClick={() => handleDeleteSimulation(loan)}
                              className="p-2 rounded-lg border border-gray-200 bg-white text-red-600 hover:bg-red-50 hover:text-red-700 transition"
                            >
                              <FaRegTrashAlt size={16} />
                            </button>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  ) : (
                    <Table.Row>
                      <Table.Cell
                        colSpan={6}
                        className="text-center py-6 text-gray-500"
                      >
                        Nenhuma simulação encontrada.
                      </Table.Cell>
                    </Table.Row>
                  )
                ) : (
                  <TableLoading cols={7} />
                )}
              </Table.Body>
            </Table>
          </div>
        </div>
      ) : (
        <EmptyTable icon="search_off">Nenhuma simulação encontrada</EmptyTable>
      )}

      {totalCount > 0 && (
        <div className="mt-3 text-center text-sm">
          Foram encontrados {totalCount} registros no total
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex overflow-x-auto justify-center mt-3">
          <Pagination
            layout="pagination"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            previousLabel=""
            nextLabel=""
            showIcons
          />
        </div>
      )}

      <Modal
        show={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        size="4xl"
      >
        <ModalHeader>Visualizar simulação</ModalHeader>
        <ModalBody className="relative min-h-40">
          {!selectedLoan || !documentTypes ? (
            <DrawerLoading />
          ) : (
            <div className="flex flex-col gap-1">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 bg-gray-200 p-2 -mb-2 text-sm">
                <a
                  className="underline"
                  href={`https://app.ampliacapital.com.br/form/${selectedLoan?.identifier}`}
                >
                  Link do fomulário do cliente
                </a>
                <div>
                  Código de acesso:{" "}
                  <span className="font-semibold">
                    {selectedLoan?.accessKey}
                  </span>
                </div>
                <div>
                  Data de criação:{" "}
                  {moment(selectedLoan?.createdAt).format("DD/MM/yyyy")}
                </div>
              </div>
              <div className="grid gap-6 xl:grid-cols-2 mt-4">
                <div className="space-y-3">
                  <TitleStripe className="mb-3">Informações da empresa</TitleStripe>
                  <div className="rounded-3xl border border-slate-200 bg-white px-6 py-5 shadow-[0_20px_40px_rgba(15,23,42,0.08)] space-y-5">
                    <div className="flex flex-col gap-1">
                      <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                        Empresa registrada
                      </p>
                      <p className="text-lg font-semibold text-slate-900">
                        {selectedLoan?.Company?.name || "Nome não informado"}
                      </p>
                      <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
                        {selectedLoan?.Company?.document ||
                          "CNPJ não informado"}
                      </p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <InfoRow label="Telefone" icon={FaPhone}>
                        <a
                          target="_blank"
                          rel="noreferrer"
                          className="text-sky-600 transition-colors hover:text-sky-700 underline"
                          href={`https://wa.me/55${selectedLoan?.Company?.phoneNumber?.replace(
                            /\D/g,
                            ""
                          )}`}
                        >
                          {selectedLoan?.Company?.phoneNumber ||
                            "Telefone não informado"}
                        </a>
                      </InfoRow>
                      <InfoRow label="Email" icon={FaEnvelope}>
                        <a
                          className="text-sky-600 transition-colors hover:text-sky-700 underline"
                          href={`mailto:${selectedLoan?.Company?.email}`}
                        >
                          {selectedLoan?.Company?.email ||
                            "Email não informado"}
                        </a>
                      </InfoRow>
                    </div>
                    <div className="border-t border-dashed border-slate-200 pt-4 space-y-2">
                      <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.3em] text-slate-500">
                        <span>Endereço completo</span>
                        <span className="text-slate-400">
                          Nº{" "}
                          {selectedLoan?.Company?.addressNumber || "—"}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700">
                        {selectedLoan?.Company?.address ||
                          "Endereço não informado"}
                      </p>
                      <p className="text-[12px] uppercase tracking-[0.3em] text-slate-600">
                        {selectedLoan?.Company?.addressNeighborhood ||
                          "Bairro não informado"}{" "}
                        ·{" "}
                        {selectedLoan?.Company?.addressCity ||
                          "Cidade não informada"}{" "}
                        · CEP {selectedLoan?.Company?.cep || "—"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <TitleStripe className="mb-3">Informações do crédito</TitleStripe>
                  <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6 py-6 text-white shadow-[0_20px_40px_rgba(15,23,42,0.4)]">
                    <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.5em] text-white/70">
                          Valor desejado
                        </p>
                        <p className="text-3xl font-semibold">
                          {formatToReal(selectedLoan?.amount)}
                        </p>
                      </div>
                      <Badge
                        color="info"
                        size="lg"
                        className="text-[12px] font-semibold uppercase tracking-[0.3em] text-white"
                      >
                        {selectedLoan?.SegmentType?.name ||
                          "Segmento não informado"}
                      </Badge>
                    </div>
                    <div className="grid gap-4 mt-5 md:grid-cols-2">
                      <div className="rounded-2xl border border-white/20 bg-white/5 p-4 shadow-sm">
                        <p className="text-[10px] uppercase tracking-[0.4em] text-white/60">
                          Nº de parcelas
                        </p>
                        <p className="text-2xl font-semibold">
                          {selectedLoan?.installments || "—"}
                        </p>
                      </div>
                      <div className="rounded-2xl border border-white/20 bg-white/5 p-4 shadow-sm">
                        <p className="text-[10px] uppercase tracking-[0.4em] text-white/60">
                          Faturamento presumido
                        </p>
                        <p className="text-2xl font-semibold">
                          {formatToReal(selectedLoan?.Company?.incomeMonth)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <TitleStripe className="mb-2">
                Documentos da empresa ({countDocs("company")})
              </TitleStripe>
              <div className="shadow-sm rounded-md p-2 border border-gray-200">
                <Timeline>
                  {getDocsByAppliesTo("company")?.map((doc) => (
                    <TimelineItem key={doc.id}>
                      <TimelinePoint />
                      <TimelineContent>
                        <TimelineTime
                          className={`font-semibold flex gap-1 items-center pt-1.5
                            ${
                              doc.uploaded
                                ? "text-green-600"
                                : "text-yellow-500"
                            }
                          `}
                        >
                          {doc.uploaded ? (
                            <div className="flex items-center gap-1">
                              <FaCheck fontSize={12} />
                              <span>Enviado</span>
                              {getUploadedDoc(doc.id)?.rejectionReason && (
                                <span
                                  color="failure"
                                  className="text-red-600 text-2xs italic"
                                >
                                  {" "}
                                  - (Rejeitado:{" "}
                                  {getUploadedDoc(doc.id).rejectionReason})
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <FaRegClock fontSize={12} />
                              <span>Pendente</span>
                            </div>
                          )}
                        </TimelineTime>
                        <TimelineTitle className="text-base">
                          {doc.name}
                        </TimelineTitle>
                        <TimelineBody className="text-sm">
                          {doc.uploaded && (
                            <div className="flex gap-2 items-center">
                              <a
                                href={getUploadedDoc(doc.id)?.fileUrl}
                                className="underline"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FaRegEye className="inline-block mr-1" />
                                Visualizar documento
                              </a>
                              {!getUploadedDoc(doc.id)?.rejectionReason && (
                                <button
                                  type="button"
                                  className="text-red-600 underline"
                                  onClick={() =>
                                    openRejectModal(getUploadedDoc(doc.id)?.id)
                                  }
                                >
                                  <IoMdClose className="inline-block" />
                                  Rejeitar documento
                                </button>
                              )}
                            </div>
                          )}
                        </TimelineBody>
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </Timeline>
              </div>
              <div>
                {selectedLoan?.Company?.Partners?.map((partner) => (
                  <div key={partner.id}>
                    <TitleStripe className="mb-3">
                      Documentos de {partner?.name?.toLowerCase()} (
                      {countDocs("partner", partner.id)})
                    </TitleStripe>
                    <div className="shadow-sm rounded-md p-2 border border-gray-200">
                      <Timeline>
                        {getDocsByAppliesTo("partner", partner.id)?.map(
                          (doc) => (
                            <TimelineItem key={`${doc.id}-${partner.id}`}>
                              <TimelinePoint />
                              <TimelineContent>
                                <TimelineTime
                                  className={`font-semibold flex gap-1 items-center pt-1.5 
                                    ${
                                      doc.uploaded
                                        ? "text-green-600"
                                        : "text-yellow-500"
                                    }
                                  `}
                                >
                                  {doc.uploaded ? (
                                    <div className="flex items-center gap-1">
                                      <FaCheck fontSize={12} />
                                      <span>Enviado</span>
                                      {getUploadedDoc(doc.id, partner.id)
                                        ?.rejectionReason && (
                                        <span
                                          color="failure"
                                          className="text-red-600 text-2xs italic"
                                        >
                                          {" "}
                                          - (Rejeitado:{" "}
                                          {
                                            getUploadedDoc(doc.id, partner.id)
                                              .rejectionReason
                                          }
                                          )
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <FaRegClock fontSize={12} />
                                      <span>Pendente</span>
                                    </div>
                                  )}
                                </TimelineTime>
                                <TimelineTitle className="text-base">
                                  {doc.name}
                                </TimelineTitle>
                                <TimelineBody className="text-sm">
                                  {doc.uploaded && (
                                    <div className="flex gap-2 items-center">
                                      <a
                                        href={
                                          getUploadedDoc(doc.id, partner.id)
                                            ?.fileUrl
                                        }
                                        className="underline"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <FaRegEye className="inline-block mr-1" />
                                        Visualizar documento
                                      </a>
                                      {!getUploadedDoc(doc.id, partner.id)
                                        ?.rejectionReason && (
                                        <button
                                          type="button"
                                          className="text-red-600 underline"
                                          onClick={() =>
                                            openRejectModal(
                                              getUploadedDoc(doc.id, partner.id)
                                                ?.id
                                            )
                                          }
                                        >
                                          <IoMdClose className="inline-block" />
                                          Rejeitar documento
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </TimelineBody>
                              </TimelineContent>
                            </TimelineItem>
                          )
                        )}
                      </Timeline>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="flex justify-center">
          <Button onClick={() => setIsOpenModal(false)}>Fechar</Button>
        </ModalFooter>
      </Modal>

      <Modal
        show={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        size="md"
        dismissible
      >
        <ModalHeader>Rejeitar Documento</ModalHeader>
        <ModalBody>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo da rejeição
            </label>
            <select
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            >
              <option value="">Selecione um motivo</option>
              {rejectionOptions.map((reason, i) => (
                <option key={i} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={confirmRejection}>Confirmar Rejeição</Button>
          <Button color="gray" onClick={() => setIsRejectModalOpen(false)}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>

      <Drawer
        title={`Editar Simulação`}
        onClose={setIsDrawerEditLoan}
        isOpen={isDrawerEditLoan}
        className={"w-full md:w-4/6 lg:w-3/6 xl:w-2/6"}
      >
        <EditQueryCustomForm
          categories={categories}
          onClose={setIsDrawerEditLoan}
          fetchQueries={() => {
            queryClient.invalidateQueries(["loans", currentPage]);
          }}
          query={selectedLoan}
          isOpen={isDrawerEditLoan}
        />
      </Drawer>

      <Drawer
        title={`Criar nova simulação`}
        onClose={setIsDrawerCreateSimulation}
        isOpen={isDrawerCreateSimulation}
        className={"w-full md:w-4/6 lg:w-3/6 xl:w-2/6"}
      >
        <CreateQueryForm
          categories={categories}
          onClose={setIsDrawerCreateSimulation}
          fetchSimulations={() => {
            queryClient.invalidateQueries(["loans", currentPage]);
          }}
          isOpen={isDrawerCreateSimulation}
        />
      </Drawer>
      <ModalDelete
        handleDelete={confirmDeleteSimulation}
        isOpen={isOpenDeleteModal}
        setOpenModal={handleCloseDeleteModal}
        title="Tem certeza que deseja excluir esta simulação?"
        isLoading={isDeletingSimulation}
      />
    </>
  );
};

export default SimulationsTable;
